import { createRouter, createWebHistory } from "vue-router";
import { allRoute } from "@/router/routes";
import { hasPermission } from "@/helpers/permissions";
import { clearSession } from "@/api/http";
import { useAuthStore } from "@/stores/auth";
import { useApiState } from "@/stores/apiState";
import NProgress from "nprogress";
import "nprogress/nprogress.css";
import { navigation, redirectToSsoSignIn } from "@/helpers/navigation";

NProgress.configure({ showSpinner: false });

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: allRoute,
});

router.beforeEach(async (routeTo, routeFrom, next) => {
  NProgress.start();
  const title = routeTo.meta.title;
  if (title) {
    document.title = title.toString();
  }
  const authRequired = routeTo.matched.some((route) => route.meta.authRequired);
  if (authRequired) {
    const authStore = useAuthStore();
    // `user` is in-memory only (no storage), so it is already null after
    // any real page refresh — this still fetches fresh on every reload
    // while avoiding a redundant call on every in-app navigation.
    if (!authStore.user) {
      try {
        await authStore.fetchUser();
      } catch (err) {
        redirectToLogin();
        // Cancel the in-app navigation; the browser is leaving for SSO.
        return next(false);
      }
    }
  }

  const requiredPermission = routeTo.meta.permission as unknown as string;
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return next({ name: "error.403" });
  }

  return next();
});

// A page's code chunk that fails to load (a deploy replaced it, or Vite
// re-optimised its dependencies) leaves the navigation dead with the progress
// bar running. Load that page fresh instead — once, so a chunk that is really
// missing can't reload forever.
const CHUNK_RELOAD_KEY = "rac:chunk-reload";
const CHUNK_LOAD_ERROR = /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i;

router.afterEach(() => {
  NProgress.done();
  sessionStorage.removeItem(CHUNK_RELOAD_KEY);

  // A failed load belongs to the page that made it: its "Try again" must not
  // follow the user to the next page. Confirmations ("Saved.") still do.
  const apiState = useApiState();
  if (apiState.retry) {
    apiState.retry = null;
    apiState.setError(false);
    apiState.setMessage("");
  }
});

router.onError((error, to) => {
  NProgress.done();
  if (!CHUNK_LOAD_ERROR.test(error?.message ?? "")) return;
  if (sessionStorage.getItem(CHUNK_RELOAD_KEY) === to.fullPath) return;
  sessionStorage.setItem(CHUNK_RELOAD_KEY, to.fullPath);
  navigation.go(to.fullPath);
});

const redirectToLogin = () => {
  clearSession();
  NProgress.done();
  redirectToSsoSignIn();
};
export default router;
